import { Box, Typography } from '@mui/material';
import { Map, Overlay, ZoomControl } from 'pigeon-maps';
import { useMemo } from 'react';
import { advisoryMapViewState } from '../../atoms';
import { type AdvisoryResult } from '../../hooks/useAdvisory';
import { useMapView } from '../../hooks/useMapView';

const STATUS_COLORS: Record<string, string> = {
    green: '#4caf50',
    yellow: '#ff9800',
    red: '#f44336',
};

const STATUS_TEXT_COLORS: Record<string, string> = {
    green: '#fff',
    yellow: '#000',
    red: '#fff',
};

const FIVE_MILES_METERS = 5 * 1609.344;

const getPixelRadius = (lat: number, zoom: number) => {
    const metersPerPixel = (2 * Math.PI * 6371000 * Math.cos((lat * Math.PI) / 180)) / (256 * Math.pow(2, zoom));
    return FIVE_MILES_METERS / metersPerPixel;
};

type Props = {
    advisories: AdvisoryResult[];
};

export const AdvisoryMap = ({ advisories }: Props) => {
    const { center, zoom, handleBoundsChanged } = useMapView(advisoryMapViewState, advisories.length ? [advisories[0].station.latitude, advisories[0].station.longitude] : [39.7283, -121.838]);

    const overlays = useMemo(() =>
        advisories.map((advisory) => ({
            name: advisory.station.name,
            coordinates: [advisory.station.latitude, advisory.station.longitude] as [number, number],
            color: advisory.status['current']?.color || 'green',
            details: advisory.status['current']?.details ?? [],
        })),
        [advisories],
    );

    return (
        <Box sx={{ display: 'flex', height: '100%' }}>
            <Box style={{ flex: 1 }}>
                <Map center={center} zoom={zoom} onBoundsChanged={handleBoundsChanged}>
                    <ZoomControl />
                    {overlays.map(({ name, coordinates, color, details }) => {
                        const radius = getPixelRadius(coordinates[0], zoom);
                        const diameter = radius * 2;
                        const bgColor = STATUS_COLORS[color];
                        const textColor = STATUS_TEXT_COLORS[color];

                        return (
                            <Overlay key={name} anchor={coordinates}>
                                <div
                                    style={{
                                        width: diameter,
                                        height: diameter,
                                        marginLeft: -radius,
                                        marginTop: -radius,
                                        borderRadius: '50%',
                                        backgroundColor: bgColor,
                                        opacity: 0.65,
                                        border: `2px solid ${bgColor}`,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        pointerEvents: 'none',
                                    }}>
                                    <Typography
                                        variant="caption"
                                        sx={{ color: textColor, fontWeight: 'bold', textAlign: 'center', px: 0.5, lineHeight: 1.2, pointerEvents: 'none' }}>
                                        {name}
                                    </Typography>
                                    {details.map((d, i) => (
                                        <Typography
                                            key={i}
                                            variant="caption"
                                            sx={{ color: textColor, textAlign: 'center', fontSize: '0.6rem', lineHeight: 1.1, pointerEvents: 'none' }}>
                                            {d}
                                        </Typography>
                                    ))}
                                </div>
                            </Overlay>
                        );
                    })}
                </Map>
            </Box>
        </Box>
    );
};
